"use client";

import { type AlertProps } from "@kyan/ui";
import { useEffect } from "react";

import { getOptionsCreditAndDebit } from "#/app/(desktop)/combo/utils/getOptionsCreditAndDebit";
import { calculateTradeFees } from "#/components/TradeFee/TradeFee.controller";
import { getPerpConstraints } from "#/config/orders";
import { useDeltaSlippage } from "#/hooks/useDeltaSlippage";
import { useMarket } from "#/store/app/portfolio/market";
import { useSlippagePercentage } from "#/store/app/portfolio/settings/slippagePercentage";
import { useComboPrices } from "#/store/combo/comboPrices";
import { useComboState } from "#/store/combo/comboState";
import { ComboPricesRowType } from "#/types/combo";
import { calcPerpPriceWithSlippage } from "#/utils/calcPerpPriceWithSlippage";
import {
  getOptionInstrumentProperties,
  getPerpInstrumentProperties,
} from "#/utils/instrument.utils";

import { getColumns } from "./PricesTable.constants";
import { useCalculateUserRiskForComboOrderbook } from "./PricesTable.hooks";

export const usePricesTableController = () => {
  const { market } = useMarket();
  const { setComboState } = useComboState();
  const { comboPrices, hasEnoughLiquidity, isLoading, hasSufficientMargin } =
    useCalculateUserRiskForComboOrderbook();
  const { setLimitNetPrices, areLimitNetPricesFilled, limitNetPrices } =
    useComboPrices();
  const deltaSlippageFactors = useDeltaSlippage();
  const { slippageFactor: perpSlippagePercentageFactor } =
    useSlippagePercentage();

  const shouldShowAlert = !hasEnoughLiquidity || !hasSufficientMargin;
  const hasValidationErrors = shouldShowAlert || !areLimitNetPricesFilled;
  const alertProps: AlertProps = {
    variant: hasEnoughLiquidity ? "error" : "warning",
    title: hasEnoughLiquidity
      ? "Insufficient margin"
      : "Insufficient liquidity",
    description: hasEnoughLiquidity
      ? "The order will not be filled due to insufficient margin."
      : "The order will not be filled due to insufficient liquidity.",
  };

  const handleLimitNetPriceChange = (
    limitNetPrice: string,
    rowType: ComboPricesRowType,
  ) => {
    setLimitNetPrices((prev: Record<string, string>) => ({
      ...prev,
      [rowType]: limitNetPrice,
    }));
  };

  const comboOptionPrices = comboPrices.filter(
    (instrument) => instrument.rowType === ComboPricesRowType.OptionsLeg,
  );
  const perpLegs = comboPrices.filter(
    (instrument) => instrument.rowType === ComboPricesRowType.PerpsLeg,
  );

  const { totalWithSlippage } = getOptionsCreditAndDebit(
    comboOptionPrices,
    deltaSlippageFactors,
  );

  const markPrices = comboPrices.map((i) => i?.markPrice);
  const markDependency = markPrices.join("-");

  useEffect(() => {
    // Set total premiums for option legs:
    handleLimitNetPriceChange(
      Math.abs(totalWithSlippage).toFixed(4),
      ComboPricesRowType.Summary,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only update once per markPrice Change;
  }, [markDependency, totalWithSlippage]);

  const perpPriceDependency = perpLegs[0]?.indexPrice;
  const priceIncrementDecimals =
    getPerpConstraints(market.underlying)?.priceDisplayPrecisionNotional ?? 0;
  const perpDirection = perpLegs[0]?.side;

  useEffect(() => {
    // Set Perp leg limit Price
    if (!perpDirection || !perpPriceDependency) return;

    const isBuy = perpDirection === "buy";
    const newLimitPriceWithSlippage = calcPerpPriceWithSlippage({
      isBuy,
      slippageFactor: perpSlippagePercentageFactor,
      indexPrice: perpPriceDependency,
      priceIncrementDecimals,
    });
    handleLimitNetPriceChange(
      newLimitPriceWithSlippage,
      ComboPricesRowType.PerpsLeg,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- update once per indexPrice update.
  }, [perpPriceDependency, perpDirection]);

  const comboOptionsFees = comboOptionPrices.map((option) => {
    const { underlying } = getOptionInstrumentProperties(option.instrument);
    return calculateTradeFees({
      amount: option.size ?? 0,
      underlying,
      isPerp: false,
      optionPrice: option.estimatedExecutionPrice ?? 0,
      indexPrice: option.indexPrice ?? 0,
    });
  });

  const comboPerpsFees = comboPrices
    .filter((instrument) => instrument.rowType === ComboPricesRowType.PerpsLeg)
    .map((instrument) => {
      const { underlying } = getPerpInstrumentProperties(instrument.instrument);
      return calculateTradeFees({
        amount: instrument.size ?? 0,
        underlying,
        isPerp: true,
        indexPrice: instrument.indexPrice ?? 0,
      });
    });

  const columns = getColumns({
    isLoading,
    comboPerpsFees,
    comboOptionsFees,
    totalWithSlippage,
    perpLegLimitPrice: limitNetPrices[ComboPricesRowType.PerpsLeg],
  });

  const resetComboState = () => {
    setComboState("instruments");
  };

  return {
    comboPrices,
    columns,
    shouldShowAlert,
    hasValidationErrors,
    alertProps,
    resetComboState,
  };
};
